/**
 * Purpose:
 * Demonstration IAppSite handler that redefines the rendered AppSite/AppView to a target
 * supplied via parameter config (DynamicAppSite.*.json). Stateless — it does not inspect
 * contextJson; the replacement is driven entirely by the handler's parameter file.
 *
 * Responsibilities:
 * - Read TargetAppSite/TargetAppView from paramJson
 * - Return the target as the AppSite/AppView to render (falls back to the requested values when unset)
 *
 * Port of C# AssemblerModule/RegistryBase/DynamicAppSite/ReplaceAppSite.cs
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { IAppSite } from '../../../../Assembler/dist/registry/iface/iAppSite.js';
import { AppSiteResult } from '../../../../Assembler/dist/registry/iface/appSiteResult.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';

export class ReplaceAppSite extends IAppSite {
    /** Gets the registry type name used for dynamic-appsite tracking. */
    get typeName(): string {
        return 'ReplaceAppSite';
    }

    /** Resolves the AppSite/AppView from the TargetAppSite/TargetAppView parameter config. */
    resolveAppSite(appSite: string, appView: string, paramJson: JsonObject, contextJson: JsonObject, configJson: JsonObject | null): AppSiteResult {
        void contextJson;
        void configJson;
        let targetAppSite = appSite;
        let targetAppView = appView;

        try {
            if (paramJson !== null) {
                // SourceAppSite filter: when set (e.g. a wwwroot-level wildcard handler that fires for every
                // AppSite), only redirect when the requested AppSite matches. Empty/absent => always redirect.
                if (paramJson.containsKey('SourceAppSite')) {
                    const source = paramJson.get('SourceAppSite').asString();
                    if (source !== null && source.length > 0 && source.toLowerCase() !== appSite.toLowerCase()) {
                        return new AppSiteResult(appSite, appView);
                    }
                }
                if (paramJson.containsKey('TargetAppSite')) {
                    const value = paramJson.get('TargetAppSite').asString();
                    if (value !== null && value.length > 0) targetAppSite = value;
                }
                if (paramJson.containsKey('TargetAppView')) {
                    // Allow an empty TargetAppView to clear the requested AppView.
                    const value = paramJson.get('TargetAppView').asString();
                    if (value !== null) targetAppView = value;
                }
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'ReplaceAppSite');
        }

        return new AppSiteResult(targetAppSite, targetAppView);
    }
}
